import React, { memo, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PathConstant } from "const";
import { NoData } from "components";
import { Device, DeviceGroup } from "../deviceManagement.helper";
import DeviceRow from "./DeviceRow";

interface DeviceTableProps {
  groupedDevices: DeviceGroup[];
  selectedDeviceIds: string[];
  onCheckboxChange: (deviceId: string, isChecked: boolean) => void;
  onLogoutDevice: (deviceId: string) => void;
  expandedGroups: Record<string, boolean>;
  onToggleGroup: (accountId: string) => void;
  isAllSelected: boolean;
  isIndeterminate: boolean;
  onSelectAll: (isChecked: boolean) => void;
  isSelectAllMode: boolean;
  excludeDeviceIds: string[];
  columnVisibility?: Record<string, boolean>;
}

/**
 * Flat Device Table — renders header with Checkbox in column 1
 * and renders every device on a separate flat row.
 */
const DeviceTable: React.FC<DeviceTableProps> = ({
  groupedDevices,
  selectedDeviceIds,
  onCheckboxChange,
  onLogoutDevice,
  isAllSelected,
  onSelectAll,
  isSelectAllMode,
  excludeDeviceIds,
  columnVisibility = {},
}) => {
  const history = useHistory();
  const { t } = useTranslation("deviceManagement");

  const isVisible = (key: string) => columnVisibility[key] !== false;

  const handleDeviceClick = (device: Device) => {
    const params = new URLSearchParams(window.location.search);
    params.set("detail", device.id);
    history.push({
      pathname: PathConstant.DEVICE_MANAGEMENT,
      search: `?${params.toString()}`,
      state: { device },
    });
  };

  const totalDevices = groupedDevices.reduce(
    (sum, group) => sum + group.devices.length,
    0
  );

  const allPageDeviceIds = useMemo(() => {
    const ids: string[] = [];
    groupedDevices.forEach((group) => {
      group.devices.forEach((device) => ids.push(device.id));
    });
    return ids;
  }, [groupedDevices]);

  const excludedInPage = allPageDeviceIds.filter((id) => excludeDeviceIds.includes(id));
  const allExcludedInPage = excludedInPage.length === allPageDeviceIds.length && allPageDeviceIds.length > 0;

  const handleSelectAllChange = () => {
    if (isSelectAllMode) {
      onSelectAll(allExcludedInPage);
    } else if (isAllSelected) {
      onSelectAll(false);
    } else {
      onSelectAll(true);
    }
  };

  const isDeviceSelected = (deviceId: string): boolean => {
    return isSelectAllMode
      ? !excludeDeviceIds.includes(deviceId)
      : selectedDeviceIds.includes(deviceId);
  };

  const sortedFlatDevices = useMemo(() => {
    const list: { device: Device; group: DeviceGroup }[] = [];
    groupedDevices.forEach((group) => {
      group.devices.forEach((device) => {
        list.push({ device, group });
      });
    });
    // Sort all devices by lastActivity DESC (most recently active devices first)
    list.sort((a, b) => Number(b.device.lastActivity || 0) - Number(a.device.lastActivity || 0));
    return list;
  }, [groupedDevices]);

  return (
    <div className="table-responsive">
      <table className="table table-vcenter card-table table-hover">
        <thead>
          <tr>
            <th className="text-center" style={{ width: "40px" }}>
              <input
                type="checkbox"
                className="form-check-input"
                checked={isSelectAllMode ? !allExcludedInPage : isAllSelected}
                onChange={handleSelectAllChange}
              />
            </th>
            {isVisible("deviceName") && <th style={{ maxWidth: "200px" }}>{t("TXT_DEVICE_HEADER_NAME")}</th>}
            {isVisible("accountName") && <th style={{ maxWidth: "160px" }}>{t("TXT_DEVICE_HEADER_ACCOUNT")}</th>}
            {isVisible("phone") && <th style={{ maxWidth: "180px" }}>{t("TXT_DEVICE_HEADER_PHONE")}</th>}
            {isVisible("role") && <th>{t("TXT_DEVICE_HEADER_ROLE")}</th>}
            {isVisible("type") && <th>{t("TXT_DEVICE_HEADER_TYPE")}</th>}
            {isVisible("os") && <th>{t("TXT_DEVICE_HEADER_OS")}</th>}
            {isVisible("status") && <th>{t("TXT_DEVICE_HEADER_STATUS")}</th>}
            {isVisible("firstLoginDate") && <th>{t("TXT_DEVICE_HEADER_FIRST_LOGIN")}</th>}
            {isVisible("version") && <th>{t("TXT_DEVICE_HEADER_VERSION")}</th>}
            {isVisible("lastActivity") && <th>{t("TXT_DEVICE_HEADER_LAST_ACTIVITY")}</th>}
            {isVisible("actions") && <th className="text-center" style={{ width: "80px" }}>{t("TXT_DEVICE_HEADER_ACTIONS")}</th>}
          </tr>
        </thead>
        <tbody>
          {sortedFlatDevices.map(({ device, group }) => (
            <DeviceRow
              key={device.id}
              group={group}
              device={device}
              isSelected={isDeviceSelected(device.id)}
              onCheckboxChange={onCheckboxChange}
              onDeviceClick={handleDeviceClick}
              onLogoutDevice={onLogoutDevice}
              columnVisibility={columnVisibility}
            />
          ))}
        </tbody>
      </table>
      {totalDevices === 0 && <NoData content={t("TXT_DEVICE_NOT_FOUND")} />}
    </div>
  );
};

export default memo(DeviceTable);